import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useInventory, useMe } from '@/api/hooks';
import { useUi } from '@/app/store';
import { Table } from '@/components/Table';
import { TarotCard } from '@/components/TarotCard';
import { Markdown, fmtTime } from '@/components/ui';
import { platform } from '@/platform';
import { Icon } from '@/components/Icon';
import { preload } from '@/lib/preload';
import { useEffect } from 'react';
import { usePaywall } from '../paywall/Paywall';
import { useReadingFlow } from './useReadingFlow';

export function ReadingScreen() {
  const nav = useNavigate();
  const { id } = useParams();
  const question = (useLocation().state as { question?: string } | null)?.question;
  const { data: me } = useMe();
  const { data: inv } = useInventory();
  const toast = useUi((s) => s.showToast);
  const { paywall, openPaywall } = usePaywall();
  const { state: s, flip, flipAll, append, retry, rate } = useReadingFlow({ question, readingId: id });
  const [rated, setRated] = useState<1 | -1 | null>(null);
  useEffect(() => { preload(s.cards.map((c) => c.imageUrl)); }, [s.cards]);

  if (!me || !inv) return null;
  const deck = inv.decks.find((d) => d.id === me.activeDeckId);
  const streaming = s.phase === 'streaming' || s.phase === 'append_streaming';
  const allFlipped = s.cards.length > 0 && s.cards.every((c) => c.flipped);
  const flippedCount = s.cards.filter((c) => c.flipped).length;
  const canAppend = s.phase === 'done' && !id && s.cards.length < me.config.maxCards;
  const goHome = () => nav('/', { replace: true });

  const onAppend = async () => {
    try { await append(); } catch (e) {
      if (e instanceof ApiError && e.code === 'INSUFFICIENT_FUNDS') { if (await openPaywall('append')) void onAppend(); }
      else toast((e as Error).message, true);
    }
  };
  const onLimit = async () => { (await openPaywall('premium')) ? nav('/reading', { state: { question: s.question }, replace: true }) : nav(-1); };

  const status = (() => {
    switch (s.phase) {
      case 'creating': return 'Тасую карты…';
      case 'dealt': return allFlipped ? '' : flippedCount ? `Переверните все карты для толкования (${flippedCount}/${s.cards.length})` : 'Переверните карты';
      case 'append_dealt': return 'Переверните новую карту';
      case 'streaming': case 'append_streaming': return 'Читаю карты…';
      case 'limit': return `${s.error}. Сброс через ${fmtTime(s.limitResetSec)}.`;
      default: return s.error ?? '';
    }
  })();

  return (
    <div className="screen no-nav">
      <div className="topbar">
        <button className="back" onClick={() => nav(-1)}><Icon name="back" size={18} />Назад</button>
        <button className="btn ghost sm" onClick={goHome}><Icon name="refresh" size={16} />Новый расклад</button>
      </div>
      <div className="stack">
        <div className="card question"><div className="eyebrow">Ваш вопрос</div><div className="q">{s.question}</div></div>

        <Table slots={me.tableSlots} items={inv.items}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '4%', width: '100%' }}>
            {s.cards.map((c, i) => (
              <div key={c.key} style={{ width: s.cards.length > 3 ? '19%' : '26%', position: 'relative' }}>
                <TarotCard card={c} back={deck?.backStyle} position={c.position} reversed={!c.upright} flipped={c.flipped} label={c.flipped ? c.name : undefined}
                  className={`${i >= 3 && !c.flipped ? 'deal' : ''}${streaming ? ' breath' : ''}`} onClick={() => flip(i)} />
              </div>
            ))}
          </div>
        </Table>

        {status && <p className="dim center">{status}</p>}
        {s.phase === 'dealt' && !allFlipped && <button className="btn ghost sm" style={{ margin: '0 auto' }} onClick={flipAll}>Перевернуть все</button>}

        {(streaming || s.phase === 'done') && (
          <div className="card lg interp"><h3><Icon name="sparkle" size={16} filled />Толкование</h3><Markdown text={streaming ? s.streamText : s.text} cursor={streaming} /></div>
        )}

        {s.phase === 'done' && (
          <div className="stack">
            {canAppend && <button className="btn ghost" onClick={onAppend}><Icon name="plus" size={18} />Дополнить расклад<span className="grow" />{!me.premium && <span className="pill sm"><Icon name="crystal" size={14} className="c-crystal" />{me.config.appendPriceCrystals}</span>}</button>}
            <div className="row spread">
              <span className="dim">Полезно?</span>
              <div className="rate">
                {([1, -1] as const).map((v) => <button key={v} className={rated === v ? 'on' : ''} onClick={() => { setRated(v); rate(v); }} aria-label={v === 1 ? 'Полезно' : 'Не полезно'}><Icon name={v === 1 ? 'thumbUp' : 'thumbDown'} size={18} /></button>)}
              </div>
            </div>
            <div className="row">
              <button className="btn ghost" onClick={() => platform.share(`Мой расклад: ${s.cards.map((c) => c.name).join(', ')}. Вопрос: ${s.question}`, location.origin)}><Icon name="share" size={18} />Поделиться</button>
              <button className="btn primary" onClick={goHome}><Icon name="refresh" size={16} />Новый расклад</button>
            </div>
            <p className="dim center">Развлекательный контент. Решения принимаете вы.</p>
          </div>
        )}

        {s.phase === 'off_topic' && <button className="btn primary" onClick={goHome}>Задать другой вопрос</button>}
        {s.phase === 'error' && <button className="btn ghost" onClick={s.readingId ? retry : goHome}>{s.readingId ? 'Повторить' : 'На главную'}</button>}
        {s.phase === 'limit' && <button className="btn warm" onClick={onLimit}><Icon name="sparkle" size={16} filled />Снять лимит с Premium</button>}
      </div>
      {paywall}
    </div>
  );
}
